import AsyncStorage from '@react-native-async-storage/async-storage';
import { OWNER_SETUP_FEATURE_VERSION } from '../redux/ownerActivationSlice';

/**
 * Transient AsyncStorage marker bridging the gap between the business-setup
 * token batch-write and the redux-persist flush of owner-setup eligibility
 * (data-model.md §2). If the process dies inside that window, session
 * bootstrap reconstructs pending eligibility from this marker. It contains
 * owner key, feature version, and stage only — no business content — and is
 * deleted once the session is ready.
 */

export const OWNER_SETUP_HANDOFF_STORAGE_KEY = 'ownerSetupSessionHandoff';

export type OwnerSetupHandoffStage = 'tokens_written' | 'eligibility_flushed';

export type OwnerSetupSessionHandoffMarker = {
  ownerKey: string;
  featureVersion: typeof OWNER_SETUP_FEATURE_VERSION;
  stage: OwnerSetupHandoffStage;
};

const VALID_STAGES: readonly OwnerSetupHandoffStage[] = [
  'tokens_written',
  'eligibility_flushed',
];

export async function writeOwnerSetupHandoffMarker(
  marker: OwnerSetupSessionHandoffMarker,
): Promise<void> {
  await AsyncStorage.setItem(
    OWNER_SETUP_HANDOFF_STORAGE_KEY,
    JSON.stringify(marker),
  );
}

/** Returns the marker, or null when absent/malformed/version-mismatched. */
export async function readOwnerSetupHandoffMarker(): Promise<OwnerSetupSessionHandoffMarker | null> {
  try {
    const raw = await AsyncStorage.getItem(OWNER_SETUP_HANDOFF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof parsed?.ownerKey !== 'string' ||
      parsed.ownerKey.length === 0 ||
      parsed.featureVersion !== OWNER_SETUP_FEATURE_VERSION ||
      !VALID_STAGES.includes(parsed.stage as OwnerSetupHandoffStage)
    ) {
      // Stale or malformed marker: remove so it cannot loop bootstrap.
      await AsyncStorage.removeItem(OWNER_SETUP_HANDOFF_STORAGE_KEY);
      return null;
    }
    return {
      ownerKey: parsed.ownerKey,
      featureVersion: OWNER_SETUP_FEATURE_VERSION,
      stage: parsed.stage as OwnerSetupHandoffStage,
    };
  } catch {
    return null;
  }
}

export async function clearOwnerSetupHandoffMarker(): Promise<void> {
  await AsyncStorage.removeItem(OWNER_SETUP_HANDOFF_STORAGE_KEY);
}
